/**
 * Service `comparateur_factures` — appels directs au service courtage.
 *
 * Skeleton aligné sur la spec architecture alpha :
 *   courtage/modules/comparateur_factures/docs/architecture-alpha.md
 *
 * Toutes les routes passent par le proxy Vite `/api/courtage/...` qui pointe
 * directement vers `homeys-api-courtage-{env}` (pas via `api-metier`).
 *
 * ⚠ **Statut alpha** : les endpoints backend sous-jacents ne sont **PAS
 * encore implémentés** (en attente de la validation de l'architecture par
 * Mathieu). Ce service est livré pour figer le contrat front ↔ back et
 * permettre le développement parallèle des pages front.
 */

import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import type {
    ComparateurFacturesAnalyse,
    ComparateurFacturesAnalyseListResponse,
    ComparateurFacturesAnalyseResponse,
    ComparateurFacturesInputs,
    ComparateurFacturesPdfUrlResponse,
    ComparateurFacturesRapportResponse,
    EnergyContract,
    EnergyContractListResponse,
    EnergyContractResponse,
    EnergyContractWritePayload,
    TypeEnergie,
} from "@modules/courtage/types/comparateur-factures"

export class ComparateurFacturesService {
    // -----------------------------------------------------------------------
    // Analyses factures
    // -----------------------------------------------------------------------

    /**
     * Crée une nouvelle analyse de facture (POST /comparateur_factures/analyse).
     * Le triple audit (intrinsèque + contrat + droit positif) tourne de manière
     * synchrone côté backend ; la réponse contient l'analyse complète avec ses
     * outputs.
     *
     * @param inputs - Inputs de l'analyse (cf. ComparateurFacturesInputs).
     * @param meta - Méta-info AnalyseV2 (`_id_company`, `_id_building`, ...).
     * @param controller - Annulation requête.
     */
    async createAnalyse(
        inputs: ComparateurFacturesInputs,
        meta: {
            _id_company: string
            _id_building: string
            _id_user?: string
            name?: string
        },
        controller?: AbortController
    ): Promise<ComparateurFacturesAnalyse> {
        return handleServiceCall<ComparateurFacturesAnalyse>(async () => {
            const { data } =
                await axios.post<ComparateurFacturesAnalyseResponse>(
                    "/api/courtage/comparateur_factures/analyse",
                    {
                        ...meta,
                        inputs,
                    },
                    {
                        signal: controller?.signal,
                    }
                )
            return data.data
        })
    }

    /**
     * Récupère une analyse existante (GET /comparateur_factures/analyse/{id}).
     */
    async getAnalyse(
        analyseId: string,
        controller?: AbortController
    ): Promise<ComparateurFacturesAnalyse> {
        return handleServiceCall<ComparateurFacturesAnalyse>(async () => {
            const { data } =
                await axios.get<ComparateurFacturesAnalyseResponse>(
                    `/api/courtage/comparateur_factures/analyse/${analyseId}`,
                    { signal: controller?.signal }
                )
            return data.data
        })
    }

    /**
     * Liste les analyses factures par building (via api-metier endpoint
     * générique, identique aux autres types d'analyses).
     */
    async listAnalysesByBuilding(
        buildingId: string,
        controller?: AbortController
    ): Promise<ComparateurFacturesAnalyse[]> {
        return handleServiceCall<ComparateurFacturesAnalyse[]>(async () => {
            const { data } =
                await axios.get<ComparateurFacturesAnalyseListResponse>(
                    `/api/metier/analyse/building/${buildingId}?type=comparateur_factures`,
                    { signal: controller?.signal }
                )
            return data.data
        })
    }

    /**
     * Récupère une URL signée S3 pour afficher le PDF facture dans le front.
     * Validité 15min, générée à la demande.
     */
    async getPdfSignedUrl(
        analyseId: string,
        controller?: AbortController
    ): Promise<{ signed_url: string; expires_at: string }> {
        return handleServiceCall(async () => {
            const { data } = await axios.get<ComparateurFacturesPdfUrlResponse>(
                `/api/courtage/comparateur_factures/analyse/${analyseId}/pdf_url`,
                { signal: controller?.signal }
            )
            return data.data
        })
    }

    /**
     * Génère le rapport PDF d'une analyse.
     * Retourne une URL signée S3 (valide 15min, comme pour pdf_url).
     */
    async generateRapport(
        analyseId: string,
        controller?: AbortController
    ): Promise<{ url: string; expires_at?: string }> {
        return handleServiceCall(async () => {
            const { data } =
                await axios.get<ComparateurFacturesRapportResponse>(
                    `/api/courtage/comparateur_factures/analyse/${analyseId}/rapport`,
                    { signal: controller?.signal }
                )
            return data.data
        })
    }

    /**
     * Supprime une analyse facture (soft-delete côté backend, cf. QU1).
     *
     * Le backend marque l'analyse comme `deleted_at = now()` et conserve un
     * tombstone (hash PDF ou `id_chorus_pro`) pour éviter que la prochaine
     * syncro Chorus Pro re-crée la facture. Idempotence préservée.
     */
    async deleteAnalyse(
        analyseId: string,
        controller?: AbortController
    ): Promise<void> {
        return handleServiceCall(async () => {
            await axios.delete(
                `/api/courtage/comparateur_factures/analyse/${analyseId}`,
                { signal: controller?.signal }
            )
        })
    }

    /**
     * Upload PDF facture (multipart) + déclenche l'analyse.
     * Le backend gère le S3 upload puis l'extraction Claude puis le triple audit.
     *
     * ⚠ NE PAS forcer `Content-Type: multipart/form-data` : axios doit
     * générer automatiquement le header avec le boundary correct. Le forcer
     * casse le parsing côté backend (400 Bad Request).
     */
    async uploadPdfAndAnalyse(
        file: File,
        meta: {
            _id_company: string
            _id_building: string
            _id_source: string
            _id_user?: string
            _id_energycontract?: string
        },
        controller?: AbortController
    ): Promise<ComparateurFacturesAnalyse> {
        return handleServiceCall<ComparateurFacturesAnalyse>(async () => {
            const formData = new FormData()
            formData.append("pdf", file)
            Object.entries(meta).forEach(([k, v]) => {
                if (v !== undefined) formData.append(k, v)
            })
            const { data } =
                await axios.post<ComparateurFacturesAnalyseResponse>(
                    "/api/courtage/comparateur_factures/pdf_upload",
                    formData,
                    {
                        signal: controller?.signal,
                        // boundary géré par axios (ne PAS forcer Content-Type).
                    }
                )
            return data.data
        })
    }

    // -----------------------------------------------------------------------
    // Contrats énergétiques (EnergyContract)
    //
    // DEV-527 : ces routes passent par la **façade api-metier** (`/api/metier`,
    // auth multi-tenant) qui dérive `_id_company` de l'utilisateur et délègue à
    // courtage (modèle/validation unique). Le `_id_company` envoyé ici est donc
    // écrasé côté serveur — gardé pour rétro-compat des appelants.
    // -----------------------------------------------------------------------

    /**
     * Liste les contrats d'une company, optionnellement filtrés par type d'énergie.
     */
    async listEnergyContracts(
        companyId: string,
        typeEnergie?: TypeEnergie,
        controller?: AbortController
    ): Promise<EnergyContract[]> {
        return handleServiceCall<EnergyContract[]>(async () => {
            const params: Record<string, string> = { _id_company: companyId }
            if (typeEnergie) params.type_energie = typeEnergie
            const { data } = await axios.get<EnergyContractListResponse>(
                "/api/metier/v5/energycontracts",
                { params, signal: controller?.signal }
            )
            return data.data
        })
    }

    /**
     * Récupère un contrat par son id.
     */
    async getEnergyContract(
        contractId: string,
        companyId: string,
        controller?: AbortController
    ): Promise<EnergyContract> {
        return handleServiceCall<EnergyContract>(async () => {
            const { data } = await axios.get<EnergyContractResponse>(
                `/api/metier/v5/energycontracts/${contractId}`,
                {
                    params: { _id_company: companyId },
                    signal: controller?.signal,
                }
            )
            return data.data
        })
    }

    /**
     * Crée un contrat énergétique.
     */
    async createEnergyContract(
        contract: EnergyContractWritePayload,
        controller?: AbortController
    ): Promise<EnergyContract> {
        return handleServiceCall<EnergyContract>(async () => {
            const { data } = await axios.post<EnergyContractResponse>(
                "/api/metier/v5/energycontracts",
                contract,
                { signal: controller?.signal }
            )
            return data.data
        })
    }

    /**
     * Met à jour un contrat énergétique existant.
     */
    async updateEnergyContract(
        contractId: string,
        contract: EnergyContractWritePayload,
        controller?: AbortController
    ): Promise<EnergyContract> {
        return handleServiceCall<EnergyContract>(async () => {
            const { data } = await axios.put<EnergyContractResponse>(
                `/api/metier/v5/energycontracts/${contractId}`,
                contract,
                { signal: controller?.signal }
            )
            return data.data
        })
    }
}
